import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, ExternalLink } from 'lucide-react';
import { useSocket } from '../../../context/SocketContext';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { useNotification } from '../NotificationProvider';


const NotificationDropdown = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const socket = useSocket();
    const navigate = useNavigate();
    const { showInfo } = useNotification();


    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || `${window.location.origin.replace(':3000', ':2222')}/api`;


    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await axios.get(`${apiBaseUrl}/notifications`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status === 'success') {
                setNotifications(response.data.data);
                setUnreadCount(response.data.data.filter(n => !n.isRead).length);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    useEffect(() => {
        if (socket) {
            const handleNewNotification = (notification) => {
                setNotifications(prev => [notification, ...prev]);
                setUnreadCount(prev => prev + 1);

                // Play notification sound
                try {
                    const audio = new Audio('/notif-modern.mp3');
                } catch (audioErr) {
                }
            };

            const handleNotificationRead = ({ notificationId }) => {
                setNotifications(prev =>
                    prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            };

            const handleAllNotificationsRead = () => {
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                setUnreadCount(0);
            };

            socket.on('new-notification', handleNewNotification);
            socket.on('notification-read', handleNotificationRead);
            socket.on('notifications-all-read', handleAllNotificationsRead);

            return () => {
                socket.off('new-notification', handleNewNotification);
                socket.off('notification-read', handleNotificationRead);
                socket.off('notifications-all-read', handleAllNotificationsRead);
            };
        }

    }, [socket]);

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('auth_token');
            await axios.put(`${apiBaseUrl}/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // State update will be handled by WebSocket 'notification-read' event
        } catch (error) {
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            await axios.put(`${apiBaseUrl}/notifications/read-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // State update will be handled by WebSocket 'notifications-all-read' event
        } catch (error) {
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors focus:outline-none"
            >
                <Bell className="h-4 w-4 stroke-[2]" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-[#0F2942] text-white text-[9px] font-bold rounded-full border border-white shadow-xs z-10 pointer-events-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {showDropdown && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowDropdown(false)}
                    ></div>
                    <div className="fixed right-10 top-16 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                            <h3 className="font-semibold text-gray-900">Notifications</h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs text-brand hover:opacity-80 font-medium flex items-center gap-1"
                                >
                                    <Check className="h-3 w-3" />
                                    Mark all as read
                                </button>
                            )}
                        </div>

                        <div className="max-h-[400px] overflow-y-auto">
                            {loading && notifications.length === 0 ? (
                                <div className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
                                </div>
                            ) : notifications.length > 0 ? (
                                notifications.map((notification) => (
                                    <div
                                        key={notification._id}
                                        className={`p-4 border-b border-gray-50 transition-colors cursor-pointer hover:bg-gray-50 flex gap-3 ${!notification.isRead ? 'bg-blue-50/30' : ''
                                            }`}
                                        onClick={() => {
                                            if (!notification.isRead) markAsRead(notification._id);
                                            if (notification.link) {
                                                setShowDropdown(false);
                                                let url = notification.link;

                                                // Handle legacy/incorrect links from backend templates
                                                const linkMap = {
                                                    '/hrms/recruitment/interviews': '/interview_scheduling',
                                                    '/hrms/learning/my-courses': '/my_training',
                                                    '/hrms/learning/assessments': '/my_performance',
                                                    '/hrms/learning/sessions': '/sessions_scheduling',
                                                    '/hrms/performance/my-goals': '/my_performance',
                                                    '/hrms/performance/team-goals': '/performance',
                                                    '/hrms/performance/reviews': '/reviews',
                                                    '/hrms/performance/my-plans': '/my_performance',
                                                    '/hrms/payroll/payslips': '/my_payslips',
                                                    '/hrms/profile': '/my_profile',
                                                    '/vendor/dashboard': '/onboarding'
                                                };

                                                // Check for exact matches or prefix matches for BGV
                                                let finalPath = url;
                                                if (linkMap[url]) {
                                                    finalPath = linkMap[url];
                                                } else if (url.startsWith('/hrms/background-verification/')) {
                                                    finalPath = url.replace('/hrms/background-verification/', '/view_background_verification/');
                                                } else if (url.startsWith('/crm/tickets/')) {
                                                    finalPath = url.replace('/crm/tickets/', '/ticket_detail/');
                                                }

                                                const relativePath = finalPath.startsWith('http')
                                                    ? new URL(finalPath).pathname
                                                    : finalPath;

                                                navigate(relativePath);
                                            }
                                        }}
                                    >
                                        <div className={`mt-1 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${!notification.isRead ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-400'
                                            }`}>

                                            <Bell className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm font-semibold truncate ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                                    {notification.title}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    {!notification.isRead && (
                                                        <span className="w-2 h-2 rounded-full bg-brand"></span>
                                                    )}
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                    </span>
                                                </div>

                                            </div>
                                            <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                                                {notification.message}
                                            </p>
                                        </div>
                                    </div>

                                ))
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Bell className="h-8 w-8 text-gray-300" />
                                    </div>
                                    <p className="text-sm">No new notifications</p>
                                </div>
                            )}
                        </div>

                        <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
                            <button
                                onClick={() => {
                                    setShowDropdown(false);
                                    navigate('/notifications');
                                }}
                                className="text-xs text-brand hover:opacity-80 font-semibold transition-colors"
                            >
                                View All Notifications
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default NotificationDropdown;
